import { RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot, type Root } from 'react-dom/client'
import {
  RUNTIME_CONFIG_URL,
  fetchApiProfile,
  loadRuntimeConfig,
  profileMismatch,
} from '@/shared/config'
import { installChunkErrorHandler } from '@/shared/lib/chunk-recovery'
import { LoadingState, applyTheme, useThemeStore } from '@/shared/ui'
import { FatalScreen, type FatalReason } from '@/widgets/app-errors'
import { AppProviders } from '../providers/app-providers'
import { createAppRouter } from '../router/create-app-router'
import { createServices } from './create-services'

/**
 * Startup sequence (docs/architecture.md §3):
 *   theme → config (validated) → backend profile checked → services → router.
 * Any failure renders a FatalScreen and NO feature route.
 */
export async function startApp(rootElement: HTMLElement): Promise<void> {
  applyTheme(useThemeStore.getState().theme)
  installChunkErrorHandler()

  const root = createRoot(rootElement)

  const result = await loadRuntimeConfig(RUNTIME_CONFIG_URL)
  if (!result.ok) {
    renderFatal(root, {
      kind: 'config',
      message: result.error.message,
      issues: result.error.issues,
    })
    return
  }
  const config = result.config

  // The backend serves only the endpoints of its own deployment profile. A module it does not
  // serve is a mistake in config.json or in MESAI_PROFILE, and it will not fix itself, so the
  // application stops here instead of offering screens that cannot work. A backend that could
  // not be asked is not a mismatch: `profileMismatch` returns null and the application starts.
  // Nothing is on screen yet and the next line waits for the backend (up to its own timeout),
  // so say that something is happening instead of leaving the page blank.
  root.render(
    <StrictMode>
      <LoadingState className="min-h-screen" />
    </StrictMode>,
  )
  const mismatch = profileMismatch(config, await fetchApiProfile(config.apiBaseUrl))
  if (mismatch) {
    renderFatal(root, { kind: 'config', message: mismatch.message, issues: mismatch.issues })
    return
  }

  void removeLegacyMockWorker()

  const services = createServices(config)
  const router = createAppRouter(services)

  root.render(
    <StrictMode>
      <AppProviders services={services}>
        <RouterProvider router={router} />
      </AppProviders>
    </StrictMode>,
  )
}

/**
 * Earlier versions answered API calls from a Service Worker (removed, ADR-0010). A browser that
 * still has it registered would keep intercepting requests, so it is unregistered once.
 */
async function removeLegacyMockWorker(): Promise<void> {
  try {
    // Not available outside a secure context.
    const container = (navigator as Partial<Navigator>).serviceWorker
    if (!container) return
    const registrations = await container.getRegistrations()
    await Promise.all(registrations.map((registration) => registration.unregister()))
  } catch {
    // Nothing to clean up, or not allowed: the worker file is gone anyway.
  }
}

function renderFatal(root: Root, reason: FatalReason): void {
  root.render(
    <StrictMode>
      <FatalScreen reason={reason} />
    </StrictMode>,
  )
}
